import { useMemo, useRef, useState } from "react";
import { useDroppable } from "@dnd-kit/core";
import { AddableCourse, CourseSummary } from "../../types/course.response";
import { getCourseKey } from "../../utils/courseUtils";
import Loading from "../loading/Loading";
import MissingCourseModal from "../shared/MissingCourseModal";

interface ChecklistEmptySlotProps {
    allCourses: { course: CourseSummary; isApplied: boolean }[];
    onAdd: (course: AddableCourse) => void | Promise<void>;
    nodeId?: string | null;
    dropId?: string;
    compact?: boolean;
    label?: string;
}

export default function ChecklistEmptySlot({
    allCourses,
    onAdd,
    nodeId = null,
    dropId = `checklist-add-slot-${nodeId ?? "unassigned"}`,
    compact = false,
    label,
}: ChecklistEmptySlotProps) {
    const [isAdding, setIsAdding] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [pendingCourseKey, setPendingCourseKey] = useState<string | null>(null);
    const [isMissingCourseModalOpen, setIsMissingCourseModalOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const isDroppable = nodeId !== null && !isAdding;
    const { setNodeRef, isOver } = useDroppable({
        id: dropId,
        data: { type: "empty-slot", nodeId },
        disabled: !isDroppable,
    });

    const filteredCourses = useMemo(() => {
        if (!searchQuery.trim()) return allCourses;
        const q = searchQuery.toLowerCase().replace(/\s+/g, "");
        return allCourses.filter(({ course }) => {
            const code = `${course.courseInfo.subject}${course.courseInfo.number}`.toLowerCase();
            const titleShort = course.courseInfo.titleShort?.toLowerCase() ?? "";
            const titleLong = course.courseInfo.titleLong?.toLowerCase() ?? "";
            return code.includes(q) || titleShort.includes(q) || titleLong.includes(q);
        });
    }, [allCourses, searchQuery]);

    const handleClickAdd = async (course: AddableCourse) => {
        const key = getCourseKey(course);
        setPendingCourseKey(key);
        try {
            await onAdd(course);
        } finally {
            setPendingCourseKey(null);
            setSearchQuery("");
            setIsSearchFocused(false);
            setIsAdding(false);
        }
    };

    const handleStartAdding = () => {
        setIsAdding(true);
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const handleSearchBlur = () => {
        setIsSearchFocused(false);
        if (!searchQuery.trim()) {
            setIsAdding(false);
        }
    };

    const handleOpenMissingCourseModal = () => {
        setSearchQuery("");
        setIsSearchFocused(false);
        setIsAdding(false);
        setIsMissingCourseModalOpen(true);
    };

    return (
        <div className="relative">
            {isAdding ? (
                <div className="flex items-center gap-2.5 px-3 py-2 rounded-md border border-dashed border-gray-300 bg-gray-50/50">
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder="Search courses..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={() => setIsSearchFocused(true)}
                        onBlur={handleSearchBlur}
                        className="w-full bg-transparent text-sm text-gray-600 placeholder-gray-400 focus:outline-none"
                    />
                </div>
            ) : compact ? (
                <button
                    onClick={handleStartAdding}
                    className="flex items-center gap-1 px-3 py-1 text-xs text-gray-400 hover:text-gray-500 transition-colors"
                >
                    <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    <span>Add another course</span>
                </button>
            ) : (
                <button
                    ref={setNodeRef}
                    onClick={handleStartAdding}
                    className={`flex items-center gap-1.5 w-full px-3 py-2.5 rounded-md border border-dashed text-sm transition-colors
                        ${isDroppable && isOver
                            ? "border-blue-400 bg-blue-50 text-blue-600"
                            : "border-gray-300 text-gray-500 bg-gray-50/60 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600"
                        }
                    `}
                >
                    <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    <span>{label ? `Add ${label} course...` : "Add completed course..."}</span>
                </button>
            )}

            {isSearchFocused && (
                <div
                    className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-52 overflow-y-auto"
                    onMouseDown={(e) => e.preventDefault()}
                >
                    {filteredCourses.length > 0 ? (
                        <div className="p-1">
                            {filteredCourses.map(({ course, isApplied }) => {
                                const key = getCourseKey(course);
                                const isPending = pendingCourseKey === key;
                                const isDisabled = isApplied || isPending || pendingCourseKey !== null;
                                return (
                                    <div
                                        key={key}
                                        className={`flex items-center py-1.5 px-2 rounded text-sm ${
                                            isApplied
                                                ? "opacity-40 cursor-default"
                                                : isPending
                                                ? "bg-blue-50 cursor-default"
                                                : isDisabled
                                                ? "opacity-50 cursor-default"
                                                : "cursor-pointer hover:bg-blue-50"
                                        }`}
                                        onClick={() => !isDisabled && handleClickAdd(course)}
                                    >
                                        <span className="text-gray-700 font-medium flex-shrink-0">
                                            {course.courseInfo.subject} {course.courseInfo.number}
                                        </span>
                                        <span className="ml-3 text-gray-700 truncate flex-1">
                                            {course.topic
                                                ? `${course.courseInfo.titleShort} - ${course.topic}`
                                                : course.courseInfo.titleLong}
                                        </span>
                                        {isPending && <Loading size="sm" className="ml-1 flex-shrink-0" />}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="py-3 text-center text-xs text-gray-400">No courses found</div>
                    )}
                    <div className="border-t border-gray-100 p-1">
                        <button
                            type="button"
                            onClick={handleOpenMissingCourseModal}
                            className="w-full rounded px-2 py-1.5 text-left text-xs font-medium text-blue-600 transition-colors hover:bg-blue-50"
                        >
                            My course not found
                        </button>
                    </div>
                </div>
            )}

            <MissingCourseModal
                isOpen={isMissingCourseModalOpen}
                onClose={() => setIsMissingCourseModalOpen(false)}
                onConfirm={onAdd}
            />
        </div>
    );
}
